import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useLocation } from 'react-router-dom';
import { getStoredAuth } from '@/shared/auth/auth-storage';
import { APP_FEATURE_GUARDS } from '@/shared/constants/app-permissions';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { useCrmChat } from '@/shared/hooks/useCrmChat';
import { CRM_CHAT_DEBUG, CRM_CHAT_ENABLED, CRM_CHAT_SOCKJS_URL, useCrmChatForeignRoomToast, } from '@/pages/crm-chat/crm-chat-setup';
import { chatUserRoomEventToRoom, resolveCurrentChatUserId } from '@/shared/model/chat-room.model';
import { fetchChatRoom } from '@/services/chat/chatApi';
import { applyInboundChatMessage, clearRoomUnread, getMyChatRooms, upsertRoomFromMemberAdded, } from '@/shared/reducers/chat-room.reducer';
import { useAppDispatch, useAppSelector } from '@/store';
const CrmChatContext = createContext(null);
/** Single shared SockJS/STOMP CRM chat session for layout, floating widget, full-page chat, and topbar unread badge. */
export function CrmChatProvider({ children }) {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const chatRooms = useAppSelector((s) => s.chatRoom.rooms);
    const session = useAppSelector((s) => s.auth.session);
    const crmProfile = useAppSelector((s) => s.auth.crmProfile);
    const onForeignRoomMessage = useCrmChatForeignRoomToast();
    const storedUsername = getStoredAuth()?.username ?? 'guest';
    const chatUserId = useMemo(() => resolveCurrentChatUserId(session, crmProfile) || storedUsername, [crmProfile, session, storedUsername]);
    const canAccessChat = useHasAnyAuthority(APP_FEATURE_GUARDS.chatAccess);
    const [widgetOpen, setWidgetOpen] = useState(false);
    const toggleWidget = useCallback(() => setWidgetOpen((prev) => !prev), []);
    const chatRoomsRef = useRef(chatRooms);
    chatRoomsRef.current = chatRooms;
    const hadConnectedRef = useRef(false);
    const isChatVisible = widgetOpen || pathname === '/crm/chat';
    const refreshRooms = useCallback(() => {
        void dispatch(getMyChatRooms());
    }, [dispatch]);
    const upsertRoomById = useCallback((roomId) => {
        void fetchChatRoom(roomId)
            .then((room) => {
            dispatch(upsertRoomFromMemberAdded(room));
        })
            .catch(() => undefined);
    }, [dispatch]);
    const onLiveMessage = useCallback((detail) => {
        dispatch(applyInboundChatMessage(detail));
        const known = chatRoomsRef.current.some((room) => room.id === detail.roomId);
        if (!known) {
            upsertRoomById(detail.roomId);
        }
    }, [dispatch, upsertRoomById]);
    const onMemberAdded = useCallback((event) => {
        dispatch(upsertRoomFromMemberAdded(chatUserRoomEventToRoom(event)));
        void fetchChatRoom(event.roomId)
            .then((room) => {
            dispatch(upsertRoomFromMemberAdded(room));
        })
            .catch(() => undefined);
    }, [dispatch]);
    const chat = useCrmChat({
        username: chatUserId,
        isChatVisible,
        enabled: CRM_CHAT_ENABLED && Boolean(getStoredAuth()) && canAccessChat,
        sockJsUrl: CRM_CHAT_SOCKJS_URL,
        onForeignRoomMessage,
        onLiveMessage,
        onMemberAdded,
        debug: CRM_CHAT_DEBUG,
    });
    /** Initial sidebar load only — new rooms arrive via STOMP `MEMBER_ADDED` on `/topic/chat.user.{username}`. */
    useEffect(() => {
        if (!CRM_CHAT_ENABLED || !getStoredAuth() || !canAccessChat)
            return;
        refreshRooms();
    }, [canAccessChat, refreshRooms]);
    /** After a reconnect, catch up rooms that may have been added while the socket was down. */
    useEffect(() => {
        if (!CRM_CHAT_ENABLED || !getStoredAuth() || !canAccessChat)
            return;
        if (chat.connectionState !== 'connected')
            return;
        if (hadConnectedRef.current) {
            refreshRooms();
        }
        hadConnectedRef.current = true;
    }, [canAccessChat, chat.connectionState, refreshRooms]);
    const { syncRooms } = chat;
    useEffect(() => {
        syncRooms(chatRooms);
    }, [syncRooms, chatRooms]);
    const totalUnread = useMemo(() => {
        const byRoom = new Map();
        for (const [id, count] of Object.entries(chat.unreadByRoom)) {
            byRoom.set(Number(id), count);
        }
        for (const room of chatRooms) {
            if (room.id == null)
                continue;
            const local = byRoom.get(room.id) ?? 0;
            const api = room.unreadCount ?? 0;
            byRoom.set(room.id, Math.max(local, api));
        }
        let sum = 0;
        for (const count of byRoom.values()) {
            sum += count;
        }
        return sum;
    }, [chat.unreadByRoom, chatRooms]);
    const selectRoom = useCallback((roomId) => {
        chat.selectRoom(roomId);
        dispatch(clearRoomUnread(roomId));
    }, [chat.selectRoom, dispatch]);
    const value = useMemo(() => ({
        ...chat,
        selectRoom,
        chatUsername: chatUserId,
        totalUnread,
        widgetOpen,
        setWidgetOpen,
        toggleWidget,
    }), [chat, chatUserId, selectRoom, totalUnread, widgetOpen, toggleWidget]);
    return _jsx(CrmChatContext.Provider, { value: value, children: children });
}
export function useCrmChatContext() {
    const ctx = useContext(CrmChatContext);
    if (!ctx) {
        throw new Error('useCrmChatContext must be used within CrmChatProvider');
    }
    return ctx;
}
